import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { RATE_META } from "@/lib_app/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import Avatar from "@/components/app/Avatar";
import {
  Activity as ActivityIcon,
  Plus,
  PartyPopper,
  XCircle,
  MessageSquareText,
  ArrowRightLeft,
  Paperclip,
  Pencil,
  RotateCcw,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const ACTION_META = {
  created:      { label: "Prospek Baru",   icon: Plus,            tint: "bg-[#DCE0E7] text-[#4E6D9A]" },
  won:          { label: "Deal 🎉",         icon: PartyPopper,     tint: "bg-[#C9F0D6] text-[#57D28A]" },
  lost:         { label: "Lost",           icon: XCircle,         tint: "bg-[#DCE0E7] text-[#626061]" },
  note:         { label: "Catatan",        icon: MessageSquareText,tint: "bg-[#DCE0E7] text-[#4E6D9A]" },
  stage_change: { label: "Ubah Stage",     icon: ArrowRightLeft,  tint: "bg-[#FFE89A] text-[#B39548]" },
  attachment:   { label: "Lampiran",       icon: Paperclip,       tint: "bg-[#DCE0E7] text-[#4E6D9A]" },
  updated:      { label: "Update Data",    icon: Pencil,          tint: "bg-[#DCE0E7] text-[#626061]" },
  reopened:     { label: "Dibuka Kembali", icon: RotateCcw,       tint: "bg-teal-100 text-teal-600" },
};

const FILTERS = [
  { key: "all",          label: "Semua",     actions: null },
  { key: "deals",        label: "Deal & Lost", actions: "won,lost,reopened" },
  { key: "notes",        label: "Catatan",   actions: "note" },
  { key: "stage_change", label: "Stage",     actions: "stage_change" },
  { key: "created",      label: "Baru",      actions: "created" },
];

function formatRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "baru saja";
  if (diff < 3600) return `${Math.floor(diff / 60)} menit lalu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} jam lalu`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} hari lalu`;
  return d.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
}

function formatDayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  const same = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (same(d, today)) return "Hari ini";
  if (same(d, yest)) return "Kemarin";
  return d.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function Activities() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [users, setUsers] = useState([]);
  const [limit, setLimit] = useState(50);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const preset = FILTERS.find((f) => f.key === filter);
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      if (preset?.actions) params.set("action", preset.actions);
      if (userFilter !== "all") params.set("user_id", userFilter);
      const [r, u] = await Promise.all([
        api.get(`/activities?${params}`),
        users.length === 0 ? api.get("/users") : Promise.resolve({ data: users }),
      ]);
      setItems(r.data.items || []);
      if (users.length === 0) setUsers(u.data);
    } catch {
      toast.error("Gagal memuat aktivitas");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter, userFilter, limit]);

  const grouped = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const key = (it.created_at || "").slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    });
    return Array.from(map.entries());
  }, [items]);

  return (
    <div className="space-y-6" data-testid="activities-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A0A3A8] mb-2 font-semibold">Aktivitas Tim</p>
          <h1 className="text-3xl md:text-[34px] tracking-tight font-bold text-[#090808]">Feed Aktivitas</h1>
          <p className="text-sm text-[#A0A3A8] mt-1">Deal, catatan, dan perubahan stage terbaru dari seluruh tim.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger data-testid="filter-actor" className="w-48 h-11 rounded-full bg-white card-soft px-5 border-transparent">
              <SelectValue placeholder="Semua orang" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua orang</SelectItem>
              {users.filter((u) => u.role !== "owner" && u.role !== "finance").map((u) => (
                <SelectItem key={u.user_id} value={u.user_id}>
                  <div className="flex items-center gap-2">
                    <Avatar name={u.name} picture={u.picture} size="sm" className="!w-5 !h-5 !text-[9px] !border-0" />
                    <span className="text-sm font-semibold">{u.nickname || u.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={load}
            data-testid="refresh-activity-btn"
            className="h-11 rounded-full bg-white card-soft border-transparent px-4 gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2" data-testid="activity-filter-chips">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`filter-chip-${f.key}`}
            className={`inline-flex items-center h-9 px-4 rounded-full text-xs font-semibold transition-colors ${
              filter === f.key
                ? "bg-[#090808] text-white shadow-[0_4px_10px_rgba(9,8,8,0.20)]"
                : "bg-white card-soft text-[#626061] hover:text-[#090808]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && items.length === 0 ? (
        <div className="text-sm text-[#A0A3A8]">Memuat...</div>
      ) : items.length === 0 ? (
        <Card className="p-10 border-0 rounded-2xl card-soft text-center" data-testid="activity-empty">
          <div className="w-14 h-14 rounded-2xl bg-[#DCE0E7] text-[#090808] flex items-center justify-center mx-auto mb-3">
            <ActivityIcon className="w-6 h-6" />
          </div>
          <div className="text-sm font-semibold text-[#090808]">Belum ada aktivitas</div>
          <div className="text-xs text-[#A0A3A8] mt-1">Perubahan tim akan tampil di sini.</div>
        </Card>
      ) : (
        <div className="space-y-8" data-testid="activity-feed">
          {grouped.map(([day, list]) => (
            <div key={day}>
              <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold mb-3 px-1">
                {formatDayLabel(day)}
              </div>
              <Card className="p-0 border-0 rounded-2xl card-soft overflow-hidden">
                <ul className="divide-y divide-[#DCE0E7]">
                  {list.map((it) => (
                    <ActivityRow key={it.id} item={it} onOpen={() => navigate(`/prospects?open=${it.prospect_id}`)} />
                  ))}
                </ul>
              </Card>
            </div>
          ))}

          {items.length >= limit && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => setLimit((l) => l + 50)}
                data-testid="load-more-btn"
                className="h-10 rounded-full bg-white card-soft border-transparent px-6 text-sm"
              >
                Muat lebih banyak
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ActivityRow({ item, onOpen }) {
  const meta = ACTION_META[item.action] || ACTION_META.updated;
  const rateMeta = RATE_META[item.rate] || RATE_META[3];
  const Icon = meta.icon;
  return (
    <li>
      <button
        onClick={onOpen}
        data-testid={`activity-row-${item.id}`}
        className="w-full text-left px-5 py-4 hover:bg-[#F4F5F7] transition-colors flex items-start gap-4"
      >
        <div className={`w-9 h-9 rounded-xl ${meta.tint} flex items-center justify-center shrink-0 mt-0.5`}>
          <Icon className="w-4 h-4" strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <Avatar name={item.actor_name} picture={item.actor_picture} size="xs" />
            <span className="font-semibold text-[#090808]">{item.actor_nickname || item.actor_name}</span>
            <span className="text-[#A0A3A8]">·</span>
            <span className="text-[11px] uppercase tracking-widest text-[#A0A3A8] font-bold">{meta.label}</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${rateMeta.bg} ${rateMeta.text} ${rateMeta.border}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${rateMeta.dot}`} /> {rateMeta.label}
            </span>
          </div>
          <div className="mt-1 text-sm text-[#090808]">
            <span className="font-semibold">{item.customer}</span>
            {item.service && <span className="text-[#A0A3A8]"> · {item.service}</span>}
          </div>
          {item.note && (
            <div className={`text-xs mt-1 line-clamp-2 ${item.action === "note" ? "text-[#626061]" : "text-[#A0A3A8]"}`}>
              {item.note}
            </div>
          )}
        </div>
        <div className="text-[11px] text-[#A0A3A8] shrink-0 tabular-nums pt-1">
          {formatRelative(item.created_at)}
        </div>
      </button>
    </li>
  );
}
